import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, FileText, Link2, TrendingUp, HelpCircle, Edit3, Copy, CheckCircle, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';

const AIOutputReview = ({ generatedContent, onAccept, onCancel, onRegenerate }) => {
  const [activeTab, setActiveTab] = useState('metadata');
  const [editedContent, setEditedContent] = useState(generatedContent);
  const [copiedField, setCopiedField] = useState(null);
  const [selectedLinks, setSelectedLinks] = useState(
    generatedContent.internal_links?.map(() => true) || []
  );

  const tabs = [
    { id: 'metadata', label: 'Metadata', icon: FileText },
    { id: 'content', label: 'Content', icon: Edit3 },
    { id: 'links', label: 'Internal Links', icon: Link2 },
    { id: 'faqs', label: 'FAQs', icon: HelpCircle },
    { id: 'seo', label: 'SEO Preview', icon: TrendingUp }
  ];

  const handleMetadataChange = (field, value) => {
    setEditedContent({
      ...editedContent,
      metadata: { ...editedContent.metadata, [field]: value }
    });
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getScoreColor = (score) => {
    const numScore = parseInt(score);
    if (numScore >= 80) return 'text-green-600';
    if (numScore >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    const numScore = parseInt(score);
    if (numScore >= 80) return 'bg-green-100';
    if (numScore >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-6xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-8 py-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-1">Review AI-Generated Article</h2>
            <p className="text-purple-100 text-sm">Review each section and edit as needed before publishing</p>
          </div>
          <div className={`px-4 py-2 rounded-lg ${getScoreBgColor(editedContent.seo_analysis.estimated_seo_score)} border-2 border-white/20`}>
            <div className="text-center">
              <div className={`text-3xl font-bold ${getScoreColor(editedContent.seo_analysis.estimated_seo_score)}`}>
                {editedContent.seo_analysis.estimated_seo_score}
              </div>
              <div className="text-xs font-semibold text-gray-600">SEO Score</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="flex overflow-x-auto px-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 bg-white'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-8 max-h-[600px] overflow-y-auto">
        <AnimatePresence mode="wait">
          {/* Metadata Tab */}
          {activeTab === 'metadata' && (
            <motion.div
              key="metadata"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-6"
            >
              {/* H1 Title */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold text-gray-700">H1 Title</Label>
                  <span className="text-xs text-gray-500">{editedContent.metadata.h1_title.length} characters</span>
                </div>
                <input
                  type="text"
                  value={editedContent.metadata.h1_title}
                  onChange={(e) => handleMetadataChange('h1_title', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">✅ Optimal length: 50-60 characters</p>
              </div>

              {/* Meta Description */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold text-gray-700">Meta Description</Label>
                  <span className="text-xs text-gray-500">{editedContent.metadata.meta_description.length} characters</span>
                </div>
                <textarea
                  value={editedContent.metadata.meta_description}
                  onChange={(e) => handleMetadataChange('meta_description', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">✅ Optimal length: 150-160 characters</p>
              </div>

              {/* Focus Keyword */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">Focus Keyword</Label>
                <input
                  type="text"
                  value={editedContent.metadata.focus_keyword}
                  onChange={(e) => handleMetadataChange('focus_keyword', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Excerpt */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold text-gray-700">Excerpt (for cards)</Label>
                  <span className="text-xs text-gray-500">{editedContent.metadata.excerpt.length} characters</span>
                </div>
                <textarea
                  value={editedContent.metadata.excerpt}
                  onChange={(e) => handleMetadataChange('excerpt', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={2}
                />
              </div>

              {/* Category */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">Category</Label>
                <input
                  type="text"
                  value={editedContent.metadata.category}
                  onChange={(e) => handleMetadataChange('category', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Tags */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">Tags</Label>
                <div className="flex flex-wrap gap-2">
                  {editedContent.metadata.tags.map((tag, index) => (
                    <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Author */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">Author (Optional)</Label>
                <input
                  type="text"
                  value={editedContent.author || ''}
                  onChange={(e) => setEditedContent({ ...editedContent, author: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., John Smith, Sarah Jones"
                />
                <p className="text-xs text-gray-500 mt-1">This will be displayed as the post author</p>
              </div>
            </motion.div>
          )}

          {/* Content Tab */}
          {activeTab === 'content' && (
            <motion.div
              key="content"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-6"
            >
              {/* Content Metrics */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-gray-900">{editedContent.seo_analysis.word_count}</div>
                  <div className="text-xs text-gray-600">Words</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-gray-900">{editedContent.content.sections.length}</div>
                  <div className="text-xs text-gray-600">H2 Sections</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-gray-900">{editedContent.internal_links.length}</div>
                  <div className="text-xs text-gray-600">Internal Links</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-gray-900">{editedContent.faqs.length}</div>
                  <div className="text-xs text-gray-600">FAQs</div>
                </div>
              </div>

              {/* Introduction */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">📝 Introduction</h4>
                <div className="text-sm text-gray-700 prose prose-sm" dangerouslySetInnerHTML={{ __html: editedContent.content.introduction }} />
              </div>

              {/* Sections */}
              {editedContent.content.sections.map((section, index) => (
                <div key={index} className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">H2: {section.h2}</h4>
                  <div className="text-sm text-gray-700 prose prose-sm" dangerouslySetInnerHTML={{ __html: section.content }} />
                </div>
              ))}

              {/* Conclusion */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">✅ Conclusion</h4>
                <div className="text-sm text-gray-700 prose prose-sm" dangerouslySetInnerHTML={{ __html: editedContent.content.conclusion }} />
              </div>
            </motion.div>
          )}

          {/* Links Tab */}
          {activeTab === 'links' && (
            <motion.div
              key="links"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              <p className="text-sm text-gray-600 mb-4">
                The AI has suggested {editedContent.internal_links.length} contextually relevant internal links. Review and approve each one.
              </p>

              {editedContent.internal_links.map((link, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg hover:border-blue-400 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-start gap-3 flex-1">
                      <input
                        type="checkbox"
                        checked={selectedLinks[index]}
                        onChange={() => {
                          const newSelected = [...selectedLinks];
                          newSelected[index] = !newSelected[index];
                          setSelectedLinks(newSelected);
                        }}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Link2 className="w-4 h-4 text-blue-600" />
                          <span className="font-mono text-sm text-blue-600">{link.url}</span>
                          <ExternalLink className="w-3 h-3 text-gray-400" />
                        </div>
                        <div className="text-sm text-gray-900 mb-2">
                          <strong>Anchor text:</strong> "{link.anchor_text}"
                        </div>
                        <div className="text-xs text-gray-600 mb-1">
                          <strong>Placement:</strong> {link.section}
                        </div>
                        <div className="text-xs text-gray-500">
                          <strong>Why:</strong> {link.reason}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {/* FAQs Tab */}
          {activeTab === 'faqs' && (
            <motion.div
              key="faqs"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              <p className="text-sm text-gray-600 mb-4">
                {editedContent.faqs.length} FAQ(s) generated to enhance SEO and provide value to readers.
              </p>

              {editedContent.faqs.map((faq, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gradient-to-r from-purple-50 to-blue-50">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      Q
                    </div>
                    <h4 className="font-semibold text-gray-900 flex-1">{faq.question}</h4>
                  </div>
                  <div className="flex items-start gap-3 ml-9">
                    <p className="text-sm text-gray-700">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {/* SEO Tab */}
          {activeTab === 'seo' && (
            <motion.div
              key="seo"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-6"
            >
              {/* Overall Score */}
              <div className="text-center p-8 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border-2 border-blue-200">
                <div className={`text-6xl font-bold mb-2 ${getScoreColor(editedContent.seo_analysis.estimated_seo_score)}`}>
                  {editedContent.seo_analysis.estimated_seo_score}
                </div>
                <div className="text-lg font-semibold text-gray-700">Estimated SEO Score</div>
                <p className="text-sm text-gray-600 mt-2">Based on content structure, keywords, and optimization</p>
              </div>

              {/* SEO Checklist */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900 mb-3">✅ SEO Optimization Checklist</h3>
                
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Title Optimized</div>
                    <div className="text-xs text-gray-600">{editedContent.metadata.h1_title.length} characters (optimal: 50-60)</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">H2 Section Structure</div>
                    <div className="text-xs text-gray-600">{editedContent.content.sections.length} sections (optimal: 4-6)</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Meta Description</div>
                    <div className="text-xs text-gray-600">{editedContent.metadata.meta_description.length} characters (optimal: 150-160)</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Internal Links</div>
                    <div className="text-xs text-gray-600">{editedContent.internal_links.length} links (optimal: 3-5)</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">FAQ Schema Ready</div>
                    <div className="text-xs text-gray-600">{editedContent.faqs.length} FAQs included</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Word Count</div>
                    <div className="text-xs text-gray-600">{editedContent.seo_analysis.word_count} words (excellent for ranking)</div>
                  </div>
                </div>
              </div>

              {/* Readability */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">📖 Readability</h4>
                <p className="text-sm text-gray-700">{editedContent.seo_analysis.readability_score}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer Actions */}
      <div className="border-t border-gray-200 px-8 py-6 bg-gray-50">
        {/* E-E-A-T Reminder */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>💡 Pro Tip:</strong> Add your expertise to make this content unique! Include company examples, personal insights, or specific data that only you can provide.
          </p>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={onRegenerate}
            >
              Regenerate
            </Button>
          </div>
          <Button
            onClick={() => onAccept(editedContent, selectedLinks)}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
          >
            <Check className="w-4 h-4 mr-2" />
            Accept & Use This Article
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIOutputReview;